import React from "react";
import { ImagePlus, X, Plus } from "lucide-react";
import ImageUploader, { resolveImageUrl } from "./ImageUploader";

/**
 * Before/After photo pairs. Each pair = { before, after, caption }.
 *
 * <BeforeAfterUploader value={[{before,after,caption}]} onChange={(arr)=>...} max={5} />
 */
export default function BeforeAfterUploader({ value = [], onChange, max = 5, testid = "before-after" }) {
    const update = (i, patch) => {
        const next = [...value];
        next[i] = { ...next[i], ...patch };
        onChange?.(next);
    };
    const addPair = () => {
        if ((value?.length || 0) >= max) return;
        onChange?.([...(value || []), { before: "", after: "", caption: "" }]);
    };
    const remove = (i) => {
        onChange?.((value || []).filter((_, idx) => idx !== i));
    };

    return (
        <div data-testid={`${testid}-uploader`} className="space-y-4">
            {value.map((pair, i) => (
                <div key={i} data-testid={`${testid}-pair-${i}`} className="relative bg-trail-tan/15 border border-ridge-line rounded-lg p-4">
                    <button
                        type="button"
                        onClick={() => remove(i)}
                        aria-label="Remove pair"
                        data-testid={`${testid}-remove-${i}`}
                        className="absolute top-2 right-2 w-7 h-7 rounded-full bg-snowcap border border-ridge-line hover:bg-red-50 hover:border-red-300 hover:text-red-600 flex items-center justify-center transition-all"
                    >
                        <X className="w-3.5 h-3.5" />
                    </button>
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <div className="text-[10px] font-bold uppercase tracking-widest text-slate-gray mb-1">Before</div>
                            <ImageUploader
                                value={pair.before}
                                onChange={(url) => update(i, { before: url })}
                                aspect="aspect-square"
                                testid={`${testid}-${i}-before`}
                                allowPaste={false}
                            />
                        </div>
                        <div>
                            <div className="text-[10px] font-bold uppercase tracking-widest text-slate-gray mb-1">After</div>
                            <ImageUploader
                                value={pair.after}
                                onChange={(url) => update(i, { after: url })}
                                aspect="aspect-square"
                                testid={`${testid}-${i}-after`}
                                allowPaste={false}
                            />
                        </div>
                    </div>
                    <input
                        type="text"
                        value={pair.caption || ""}
                        onChange={(e) => update(i, { caption: e.target.value })}
                        placeholder="Caption (optional) — what changed?"
                        data-testid={`${testid}-${i}-caption`}
                        className="mt-3 w-full px-3 py-2 rounded-md border border-ridge-line bg-snowcap text-sm focus:border-ember-orange outline-none"
                        maxLength={200}
                    />
                </div>
            ))}
            {(value?.length || 0) < max && (
                <button
                    type="button"
                    onClick={addPair}
                    data-testid={`${testid}-add-btn`}
                    className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-md border-2 border-dashed border-ridge-line text-slate-gray hover:border-ember-orange hover:text-ember-orange transition-all bg-trail-tan/15"
                >
                    <ImagePlus className="w-4 h-4" />
                    <span className="text-sm font-bold">Add a before / after pair</span>
                </button>
            )}
        </div>
    );
}

/**
 * Public renderer used on AdventureDetail. Accepts the same shape and renders
 * a comparison row with optional caption.
 */
export function BeforeAfterGrid({ pairs }) {
    if (!pairs?.length) return null;
    return (
        <div data-testid="before-after-grid" className="space-y-4">
            {pairs.map((p, i) => (
                <figure key={i} data-testid={`ba-${i}`} className="bg-snowcap border border-ridge-line rounded-lg overflow-hidden">
                    <div className="grid grid-cols-2">
                        <div className="relative aspect-square bg-trail-tan">
                            {p.before && <img src={resolveImageUrl(p.before)} alt="Before" className="w-full h-full object-cover" />}
                            <span className="absolute top-2 left-2 px-2 py-0.5 rounded bg-midnight-pine/80 text-white text-[10px] font-bold uppercase tracking-widest">Before</span>
                        </div>
                        <div className="relative aspect-square bg-trail-tan">
                            {p.after && <img src={resolveImageUrl(p.after)} alt="After" className="w-full h-full object-cover" />}
                            <span className="absolute top-2 left-2 px-2 py-0.5 rounded bg-ember-orange text-white text-[10px] font-bold uppercase tracking-widest">After</span>
                        </div>
                    </div>
                    {p.caption && (
                        <figcaption className="px-4 py-2 text-xs text-granite italic">{p.caption}</figcaption>
                    )}
                </figure>
            ))}
        </div>
    );
}
